/**
 * useTargetsData — data-fetching hook for Cibles (TargetsPanel).
 *
 * Manages loading targets, summary (manifest), and marque list.
 * Handles the polling loop while an import/geocode job is running.
 * Returns state + loaders so the page component owns write operations.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type TargetCategory, type TargetsManifest } from '@traaker/shared';
import { api } from '../../services/api';
import { type TargetRow } from './CATEGORIES';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseTargetsDataOptions {
  brandId: string;
  activeBrand: any;  // the full Brand object from BrandContext
  activeTab: TargetCategory;
  tagFilter: number[];
  setError: (msg: string | null) => void;
}

export interface UseTargetsDataResult {
  manifest: TargetsManifest | null;
  setManifest: React.Dispatch<React.SetStateAction<TargetsManifest | null>>;
  rows: TargetRow[];
  setRows: React.Dispatch<React.SetStateAction<TargetRow[]>>;
  marques: TargetRow[];
  setMarques: React.Dispatch<React.SetStateAction<TargetRow[]>>;
  loading: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageCursors: (string | null)[];
  setPageCursors: React.Dispatch<React.SetStateAction<(string | null)[]>>;
  hasNextPage: boolean;
  setHasNextPage: React.Dispatch<React.SetStateAction<boolean>>;
  loadSummary: () => Promise<void>;
  loadPage: (targetPage: number, cursors: (string | null)[]) => Promise<void>;
  loadRows: () => void;
  loadMarques: () => Promise<void>;
}

export function useTargetsData({
  brandId,
  activeBrand,
  activeTab,
  tagFilter,
  setError,
}: UseTargetsDataOptions): UseTargetsDataResult {
  const { t } = useTranslation();

  const [manifest, setManifest] = useState<TargetsManifest | null>(null);
  const [rows, setRows] = useState<TargetRow[]>([]);
  const [marques, setMarques] = useState<TargetRow[]>([]);
  const [loading, setLoading] = useState(false);
  // Cursor-based pagination. pageCursors[i] = cursor to fetch page i.
  // Cached so ← is instant (no re-fetch from page 0).
  const [pageCursors, setPageCursors] = useState<(string | null)[]>([null]);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  /** True while a manifest job is being polled — drives the one-shot reload when it clears. */
  const pollingRef = useRef(false);
  /** One root-marque bootstrap attempt per mount (StrictMode double-invokes effects). */
  const bootstrapRef = useRef(false);

  // ── Data loading ──

  const loadSummary = useCallback(async () => {
    if (!brandId) return;
    try { setManifest(await api.get(`/brands/${brandId}/targets/summary`) as TargetsManifest); } catch { /* empty */ }
  }, [brandId]);

  const loadPage = useCallback(async (targetPage: number, cursors: (string | null)[]) => {
    if (!brandId) return;
    setLoading(true);
    try {
      // contenu rows are tiny (one URL field) so a larger page reduces domain-group splitting
      // across pages. produit stays small because product pages are richer.
      const pageSize = activeTab === 'produit' ? '10' : activeTab === 'contenu' ? '200' : '50';
      const params = new URLSearchParams({ category: activeTab, limit: pageSize });
      // Sent to the API rather than filtered here: it walks every shard of the partition, so a
      // client-side filter would only ever see the rows of the page already loaded and would
      // report "none" for a tag whose cibles sit on page 2.
      if (tagFilter.length) params.set('tags', tagFilter.join(','));
      const cursor = cursors[targetPage] ?? undefined;
      if (cursor) params.set('cursor', cursor);
      const data = await api.get(`/brands/${brandId}/targets?${params}`) as { items: TargetRow[]; nextCursor: string | null };
      setRows(data.items);
      setPage(targetPage);
      setHasNextPage(!!data.nextCursor);
      if (data.nextCursor) {
        setPageCursors((prev) => {
          const next = [...prev];
          next[targetPage + 1] = data.nextCursor;
          return next;
        });
      }
    } catch (e: any) { setError(e?.message ?? t('cibles.errors.generic')); } finally { setLoading(false); }
  }, [brandId, activeTab, tagFilter]);

  const loadRows = useCallback(() => {
    setPageCursors([null]);
    setPage(0);
    setHasNextPage(false);
    loadPage(0, [null]);
  }, [loadPage]);

  const loadMarques = useCallback(async () => {
    if (!brandId || !activeBrand) return;
    try {
      const data = await api.get(`/brands/${brandId}/targets?category=marque&limit=100`) as { items: TargetRow[] };

      // Bootstrap the root brand marque from the brand's own name/aliases/domain — but
      // ONLY when the brand has no marque target at all.
      //
      // This used to key on "no marque whose name equals the brand's name", which made a
      // page view a WRITE: renaming a brand left the existing marque under the old name,
      // so every visit (this panel is also embedded in GeoStrategy) posted another marque.
      // An empty partition is the only state where creating is unambiguously additive.
      // `bootstrapRef` keeps StrictMode's double-invoked effect to one POST.
      if (data.items.length === 0 && !bootstrapRef.current) {
        bootstrapRef.current = true;
        const fields: { name: string; value: string }[] = [];
        if (activeBrand.name) fields.push({ name: 'name', value: activeBrand.name });
        // Pull aliases from the brand strategy (the old brand-level aliases field)
        for (const alias of (activeBrand as any).strategy?.aliases ?? []) {
          if (alias?.trim()) fields.push({ name: 'alias', value: alias.trim() });
        }
        // Domain from the brand URL
        if (activeBrand.url) {
          const domain = activeBrand.url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
          if (domain) fields.push({ name: 'domain', value: domain });
        }
        if (fields.length > 0) {
          await api.post(`/brands/${brandId}/targets`, {
            targets: [{
              ownerId: 'own',
              category: 'marque',
              label: activeBrand.name,
              fields,
            }],
          }).catch(() => { /* silent — Lambda may not be deployed yet */ });
          const refreshed = await api.get(`/brands/${brandId}/targets?category=marque&limit=100`) as { items: TargetRow[] };
          setMarques(refreshed.items);
          return;
        }
      }

      setMarques(data.items);
    } catch { /* silent */ }
  }, [brandId, activeBrand]);

  useEffect(() => {
    setRows([]); setPageCursors([null]); setPage(0); setHasNextPage(false);
    loadSummary(); loadRows();
    // `tagFilter` belongs here and not in an effect of its own: the filter is applied
    // server-side, so changing it invalidates every cached cursor exactly as a tab switch
    // does. Clearing `searchMode` with it is deliberate — the search endpoint knows nothing
    // about tags, so leaving results on screen would show rows the filter excludes.
    // Safe as a dep: `useState([])` keeps one array identity, so mount still loads once.
  }, [brandId, activeTab, tagFilter]);

  useEffect(() => { bootstrapRef.current = false; loadMarques(); }, [brandId]);

  // Poll the manifest while a job is running.
  //
  // The interval handle is EFFECT-LOCAL on purpose: a ref that the cleanup cleared but
  // never nulled made the import→geocode transition (both deps flip in the same commit,
  // so the effect re-runs) see a truthy ref and skip re-arming — polling stopped for
  // good and the geocode banner froze at 0/N.
  useEffect(() => {
    const hasJob = !!(manifest?.importJob || manifest?.geocodeJob);
    if (!hasJob) {
      // A job we were watching has just cleared → refresh the table once.
      if (pollingRef.current) { pollingRef.current = false; loadRows(); }
      return;
    }
    pollingRef.current = true;
    const id = setInterval(loadSummary, 5000);
    return () => clearInterval(id);
  }, [!!manifest?.importJob, !!manifest?.geocodeJob]);

  return {
    manifest, setManifest,
    rows, setRows,
    marques, setMarques,
    loading,
    page, setPage,
    pageCursors, setPageCursors,
    hasNextPage, setHasNextPage,
    loadSummary, loadPage, loadRows, loadMarques,
  };
}
